import { apiRequest, type CardDto } from '../board/api'
import type { SearchHitDto, StatusFlagDto, VendorInvoiceDto } from '../ledger/api'
import type { ArCheckDto, RetainerApplyDto } from './ar-check'

export type BooksOrg = '847122427' | '914269936'
export type CoverageDomain = 'phone' | 'it' | 'macd'
export type CoverageDecision = 'covered' | 'billable' | 'partial' | 'unknown'

export interface CoverageDto {
  domain: CoverageDomain
  decision: CoverageDecision
  basis: string
  basis_contract_id?: string | null
  decided_by: 'code' | 'riley' | 'user'
  decided_at?: string | null
  /** The person who recorded a user decision. Absent on an agreement read, and for roles that never see coverage. */
  decided_by_name?: string | null
  coverage_decision_id?: string | null
  contracts?: string[]
}

export interface ReadyToBillRowDto {
  case_id: string
  case_number: string
  customer_display?: string | null
  closed_for_conversation_at?: string | null
  books_org_id?: BooksOrg | null
  our_minutes_billable_via_desk: number
  contractor_minutes: number
  contractor_invoice_total?: string | number | null
  prepared_total?: string | number | null
  loaded_cost?: string | number | null
  margin?: string | number | null
  double_bill_risk: boolean
  needs_info: boolean
  /** Additive display fields accepted by the web while W2 aligns the W0 row contract to §5.7. */
  contractor_display?: string | null
  contractor_invoice_number?: string | null
  coverage_summary?: string | null
  prepared_line_summary?: string | null
  /** #440: every staff minute on the case, billable or not (the row used to show only billable-via-Desk). */
  our_minutes_total?: number
  billing_state?: BillingState
  /** #746 1.6: billing's question to the case owner. "waiting" until they answer, then "answered". */
  needs_info_state?: 'waiting' | 'answered' | null
  needs_info_note?: string | null
  needs_info_code?: string | null
  books_invoice_status?: 'draft' | 'sent' | 'paid' | 'void' | 'unknown' | null
  books_invoice_number?: string | null
  books_invoice_url?: string | null
  /** Approved contractor visits that no live customer line names as a source. */
  uncovered_work_order_numbers?: string[]
  lea_notified_at?: string | null
  lea_notification_state?: string | null
}

export interface BilledInvoiceRowDto {
  case_id: string
  case_number: string
  customer_display?: string | null
  closed_for_conversation_at?: string | null
  books_org_id: BooksOrg
  books_invoice_id: string
  books_invoice_number: string
  books_invoice_url?: string | null
  status: 'draft' | 'sent' | 'paid'
  posted_total: string | number
  books_total: string | number
  currency: string
  status_observed_at?: string | null
  uncovered_work_order_numbers: string[]
  lines: BilledInvoiceLineDto[]
}

export interface BilledInvoiceLineDto {
  line_id: string
  sku: string
  quantity: string | number
  rate: string | number
  description: string
  tax_name?: string | null
  amount: string | number
}

export type BillingState =
  | 'ready_to_post'
  | 'onsite_billable'
  | 'build_lines'
  | 'waiting_on_invoice'
  | 'enter_invoice_hours'
  | 'in_books'
  | 'needs_line'
  | 'needs_coverage'
  | 'waiting_contractor_invoice'

export interface OnsiteVisitDto {
  work_order_id: string
  wo_number: string
  contractor_display?: string | null
  assignee_kind: string
  minutes: number | null
  visit_date?: string | null
  billed_hours: string | null
  /** #746 1.6: scheduled hours and the work order's not-to-exceed; null is unknown, never zero. */
  booked_hours?: string | null
  nte_hours?: string | null
  over_nte?: boolean
}

/** #439: onsite visits against the agreement. The suggestion is a proposal for a person; nothing bills from it. */
export interface OnsiteDto {
  state: 'billable' | 'covered' | 'unknown' | 'none'
  reason: string
  domain?: CoverageDomain | null
  basis?: string | null
  visits: OnsiteVisitDto[]
  suggestion?: {
    books_org_id: BooksOrg
    sku: string
    labour_source: 'packet'
    qty_hours: string
    unit_rate: string
    amount: string
    /** #952: a current T&M quote wins, then the agreement's onsite rate, then its rate, then the standard rate. */
    rate_source: 'quote' | 'agreement_onsite' | 'conduit_note' | 'standard'
    /** Null when the quote prices the hours (no visit minimum). */
    minimum_hours: string | null
    description: string
    work_order_ids: string[]
    /** #952: how the suggestion is priced, in one sentence. */
    pricing_note?: string | null
    quote_ref?: string | null
    includes_travel?: boolean | null
    hours_source?: 'invoice' | 'work_order' | null
    /** Quote-priced hours per work order id, as preparing that visit alone would bill them. */
    visit_hours?: Record<string, string> | null
    /** Why the quote's hours could not be read (#953); preparing the line would refuse with the same reason. */
    hours_problem?: string | null
  } | null
  blocked_by?: string | null
}

/** #957: one confirmed contractor bill's share. Cost figures are absent for roles that may not see cost. */
export interface StoryBillDto {
  invoice_id: string
  invoice_number?: string | null
  contractor_display?: string | null
  before_tax?: string | null
  tax?: string | null
  total?: string | null
  onsite_hours?: string | null
  travel_hours?: string | null
  has_file?: boolean
  file_name?: string | null
}

export type PriceKind = 'quote_time_and_materials' | 'fixed_quote' | 'choose_basis' | 'quote_changed' | 'agreement' | 'standard'

/** #957: the Money section top to bottom: cost, hours, price basis and margin before tax. */
export interface MoneyStoryDto {
  bills: StoryBillDto[]
  cost_before_tax?: string | null
  tax?: string | null
  total?: string | null
  hours: {
    invoice_onsite_hours?: string | null
    invoice_travel_hours?: string | null
    invoice_hours?: string | null
    booked_hours?: string | null
    work_orders: string[]
    gap?: string | null
  }
  price: {
    kind: PriceKind
    sentence: string
    quote_ref?: string | null
    hourly_rate?: string | null
    includes_travel?: boolean | null
    problem?: string | null
  }
  margin?: {
    state: 'ok' | 'no_lines' | 'no_cost'
    revenue_before_tax?: string | null
    contractor_cost_before_tax?: string | null
    staff_cost?: string | null
    amount?: string | null
    percent?: string | null
  } | null
}

export interface BooksLinkDto {
  books_org_id: BooksOrg
  books_org_label: string
  books_customer_id: string
  books_name?: string | null
  state: 'confirmed' | 'inferred' | 'from_line'
  method: string
  confirmed_by_name?: string | null
  evidence: string[]
  shared_with_customers: number
}

export interface BooksCustomerCandidateDto {
  books_org_id: BooksOrg
  books_customer_id: string
  books_name?: string | null
  match: 'confirmed' | 'inferred' | 'from_line' | 'crm_link' | 'exact_name' | 'search'
  confident: boolean
  evidence: string[]
  shared_with_customers: number
}

export interface SalesInvoiceContextDto {
  state: 'linked' | 'not_linked' | 'unreachable'
  opportunity_ref?: string | null
  deal_name?: string | null
  stage?: string | null
  deal_url?: string | null
  quote_refs: string[]
  quote_ref?: string | null
  quote_url?: string | null
  quote_total?: string | number | null
  currency?: string | null
  last_synced_at?: string | null
  billing_basis?: 'fixed_quote' | 'time_and_materials' | null
  basis_state?: 'unset' | 'current' | 'stale'
  quote_lines?: SalesQuoteLineDto[]
}

export interface SalesQuoteLineDto {
  id: string
  position: number
  product_name: string
  description?: string | null
  default_description?: string | null
  quantity?: string | number | null
  list_price?: string | number | null
  total?: string | number | null
}

export interface SuggestedBillingLineDto {
  books_org_id: BooksOrg
  books_customer_id?: string | null
  books_customer_name?: string | null
  customer_match?: string | null
  sku: string
  labour_source: 'desk_time' | 'packet' | 'none'
  domain?: CoverageDomain | null
  service_mode?: 'remote' | 'onsite'
  actual_minutes?: number
  billed_minutes?: number
  qty_hours: string
  unit_rate: string
  amount: string
  rate_source?: string
  rounding_basis?: string
  description: string
  source_time_entry_ids: string[]
  source_work_order_ids: string[]
  tax_label?: string | null
  tax_rate_pct?: string | null
  tax_amount?: string | null
  total_with_tax?: string | null
}

export interface CostBreakdownDto {
  staff_cost?: string | number | null
  staff_minutes?: number
  contractor_cost?: string | number | null
  contractor_cost_basis?: 'time_entries' | 'contractor_invoices' | null
  uninvoiced_work_orders?: string[]
}

export interface LineDefaultsDto {
  hourly_rate: string | number
  rate_source: 'conduit_note' | 'standard'
  currency: string
}

export interface ResolutionDto {
  state: 'ok' | 'none' | 'unavailable' | 'connect_desk'
  /** #648 on_desk: Desk holds it. draft: Patchbay has one that is not on Desk yet. */
  placement?: 'on_desk' | 'draft' | null
  text?: string | null
  /** Desk's words, or the local draft, for a correction. `text` may be the invoice scrub. */
  edit_text?: string | null
  desk_ticket_id?: string | null
  ticket_number?: string | null
  /** #512 codes removed from generated text. Never the removed words. */
  stripped?: string[]
}

export interface BillingLineInDto {
  books_org_id: BooksOrg
  books_customer_id?: string | null
  sku: string
  qty_hours?: string | null
  unit_rate?: string | null
  amount?: string | null
  description?: string | null
  labour_source: 'desk_time' | 'packet' | 'none'
  domain?: CoverageDomain | null
  source_time_entry_ids?: string[]
  source_work_order_ids?: string[]
  source_quote_line_id?: string | null
}

export interface BillingLineDto {
  id: string
  case_id: string
  books_org_id: BooksOrg
  books_customer_id?: string | null
  sku: string
  qty_hours?: string | number | null
  unit_rate?: string | number | null
  amount?: string | number | null
  description?: string | null
  labour_source: 'desk_time' | 'packet' | 'none'
  state: 'prepared' | 'posted' | 'not_billable' | 'void'
  books_invoice_id?: string | null
  /** Present when the line was prepared against one domain. The packet uses it to keep that domain in view. */
  domain?: CoverageDomain | null
  coverage_decision_id?: string | null
  quote_basis?: 'fixed_quote' | null
  sales_quote_ref?: string | null
  sales_quote_fingerprint?: string | null
  source_quote_line_id?: string | null
}

export interface BooksItemDto {
  item_id: string
  sku: string
  name: string
  rate: string | number
  active: boolean
}

/** Absence/incompleteness is distinct from a successful empty Books catalogue (#803). */
export type BooksItemsDto =
  | { state: 'ok'; items: BooksItemDto[] }
  | { state: 'unreachable' | 'scope_missing' | 'incomplete'; items?: never; scope?: string | null }

export type BooksInvoiceStatus = 'draft' | 'sent' | 'paid' | 'void' | 'unknown'
export type BooksInvoiceCreateState = 'claimed' | 'linked' | 'scope_missing' | 'failed' | 'needs_reconciliation'
export type BooksInvoiceSyncState = 'pending' | 'ok' | 'unreachable' | 'not_found' | 'scope_missing' | 'incomplete' | 'needs_reconciliation'

export interface BooksInvoiceLineLinkDto {
  billing_line_id: string
  books_invoice_id: string
  books_invoice_number: string
}

export type BooksRetainerState =
  | 'none'
  | 'ambiguous'
  | 'unpaid'
  | 'paid_available'
  | 'exhausted'
  | 'unreachable'
  | 'scope_missing'
  | 'incomplete'

/** Exact Books retainer/deposit evidence. Missing values stay missing; the UI never guesses an application. */
export interface BooksRetainerDto {
  state: BooksRetainerState
  retainer_invoice_id?: string | null
  retainer_invoice_number?: string | null
  reference_number?: string | null
  amount?: string | number | null
  available_amount?: string | number | null
  currency?: string | null
}

/** The last authoritative Books read. `unreachable` retains these last-known fields instead of erasing them. */
export interface BooksCustomerInvoiceDto {
  id: string
  case_id: string
  books_org_id: string
  books_customer_id: string
  reference_number: string
  books_invoice_id?: string | null
  books_invoice_number?: string | null
  books_invoice_url?: string | null
  status: BooksInvoiceStatus
  create_state: BooksInvoiceCreateState
  last_synced_at?: string | null
  sync_state: BooksInvoiceSyncState
  notified_at?: string | null
  notification_state?: string | null
  /** Gross total returned by Books, including Books-calculated tax. */
  total?: string | number | null
  /** Null means no exact deposit match; omitted supports older cached responses during rollout. */
  retainer?: BooksRetainerDto | null
  billing_basis?: 'fixed_quote' | 'time_and_materials' | null
  sales_quote_ref?: string | null
  links: BooksInvoiceLineLinkDto[]
}

export type CaseBooksInvoiceDto = { state: 'empty'; invoice?: null } | { state: 'ok'; invoice: BooksCustomerInvoiceDto }

export interface PassthroughShareDto {
  invoice_id: string
  invoice_number?: string | null
  hours?: string | number | null
  amount?: string | number | null
  state: 'covered' | 'billable' | 'partial' | 'unknown'
  customer_amount?: string | number | null
  reason: string
}

export interface BillingPacketDto {
  case_id: string
  summary_customer_safe?: string | null
  our_time: Record<string, unknown>[]
  contractor_time: Record<string, unknown>[]
  contractor_invoices: VendorInvoiceDto[]
  /** Suggested customer lines for this case's shares. A covered share has no customer amount. */
  passthrough_shares?: PassthroughShareDto[]
  coverage: CoverageDto[]
  lines: BillingLineDto[]
  books_link?: Record<string, unknown> | null
  loaded_cost?: string | number | null
  margin?: string | number | null
  purchase_orders?: PurchaseOrderBillingDto[]
  books_links?: BooksLinkDto[]
  books_customer_candidates?: BooksCustomerCandidateDto[]
  customer_is_condo?: boolean
  cost_breakdown?: CostBreakdownDto | null
  line_defaults?: LineDefaultsDto | null
  onsite?: OnsiteDto | null
  suggested_lines?: SuggestedBillingLineDto[]
  sales?: SalesInvoiceContextDto
  /** #839: flags beside the lines and the margin; money keys are absent for roles that may not see them. */
  ar_check?: ArCheckDto | null
  /** #839: the paid deposit that can be applied to the Books draft. */
  retainer_apply?: RetainerApplyDto | null
  /** #957: cost, hours, price basis and margin before tax, in one reading. */
  money_story?: MoneyStoryDto | null
}

export interface BooksBillDto {
  id?: string | null
  number?: string | null
  status?: string | null
  date?: string | null
  amount?: string | number | null
  /** Zoho Books bill page. Absent until a Books bill id is stored. */
  books_url?: string | null
}

export interface PurchaseOrderBillingDto {
  work_order_id: string
  work_order_number: string
  books_po_id?: string | null
  books_po_number?: string | null
  books_org_id?: string | null
  /** Zoho Books PO page. Absent until a Books record id is stored. */
  books_url?: string | null
  pricing_basis?: string | null
  po_amount?: string | number | null
  po_state: 'ok' | 'nothing_matched' | 'unreachable' | 'not_linked'
  po_status?: string | null
  bills_state: 'ok' | 'nothing_matched' | 'unreachable' | 'not_linked'
  bills: BooksBillDto[]
}

export interface PageDto<T> {
  items: T[]
  next_cursor?: string | null
  total?: number | null
}

export interface BillingFilters {
  view?: 'active' | 'billed'
  periodFrom?: string
  periodTo?: string
  booksOrgId?: BooksOrg
  domain?: CoverageDomain
  hasContractorInvoice?: boolean
  customerId?: string
  search?: string
}

function query(filters: BillingFilters): string {
  const params = new URLSearchParams()
  if (filters.periodFrom) params.set('period_from', filters.periodFrom)
  if (filters.periodTo) params.set('period_to', filters.periodTo)
  if (filters.booksOrgId) params.set('books_org_id', filters.booksOrgId)
  if (filters.domain) params.set('domain', filters.domain)
  if (filters.hasContractorInvoice !== undefined) params.set('has_contractor_invoice', String(filters.hasContractorInvoice))
  if (filters.customerId) params.set('customer_id', filters.customerId)
  if (filters.search) params.set('q', filters.search)
  const encoded = params.toString()
  return encoded ? `?${encoded}` : ''
}

export interface BillingTestSource {
  readyToBill: (filters: BillingFilters) => Promise<PageDto<ReadyToBillRowDto>>
  billedHistory?: (filters: BillingFilters) => Promise<PageDto<BilledInvoiceRowDto>>
  packet: (caseId: string) => Promise<BillingPacketDto>
  caseBooksInvoice?: (caseId: string) => Promise<CaseBooksInvoiceDto>
  poExcessFlags?: () => Promise<StatusFlagDto[]>
  resolution?: (caseId: string) => Promise<ResolutionDto>
  pushResolution?: (caseId: string, text: string, deskTicketId?: string | null) => Promise<ResolutionDto>
}

let testDefault: BillingTestSource | null = null
const LIVE_OFF = () => !(globalThis as { __PATCHBAY_LIVE_API__?: boolean }).__PATCHBAY_LIVE_API__

/**
 * src/test/setup.ts installs a fixture-backed source for every vitest run, so no fixtures module is ever imported by
 * production code. A test sets `globalThis.__PATCHBAY_LIVE_API__ = true` to exercise the real fetches instead.
 */
export function installBillingTestDefault(source: BillingTestSource | null): void {
  if (import.meta.env.MODE !== 'test') throw new Error('Billing test default is test-only')
  testDefault = source
}

export async function fetchReadyToBill(filters: BillingFilters): Promise<PageDto<ReadyToBillRowDto>> {
  if (testDefault && LIVE_OFF()) return testDefault.readyToBill(filters)
  return apiRequest<PageDto<ReadyToBillRowDto>>(`/api/v1/billing/ready${query(filters)}`)
}

export async function fetchBilledHistory(filters: BillingFilters): Promise<PageDto<BilledInvoiceRowDto>> {
  if (testDefault && LIVE_OFF()) return testDefault.billedHistory?.(filters) ?? { items: [], total: 0 }
  return apiRequest<PageDto<BilledInvoiceRowDto>>(`/api/v1/billing/billed${query(filters)}`)
}

export async function fetchBillingPacket(caseId: string): Promise<BillingPacketDto> {
  if (testDefault && LIVE_OFF()) return testDefault.packet(caseId)
  return apiRequest<BillingPacketDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/packet`)
}

/** Durable local projection; this GET does not call Books and is deliberately separate from the ordinary packet. */
export async function fetchCaseBooksInvoice(caseId: string): Promise<CaseBooksInvoiceDto> {
  if (testDefault && LIVE_OFF()) return testDefault.caseBooksInvoice?.(caseId) ?? { state: 'empty' }
  return apiRequest<CaseBooksInvoiceDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/books-invoice`)
}

/** Case-page coverage. Does not use the packet fixture: that read pulls Books purchase orders. */
export async function fetchCaseCoverage(caseId: string): Promise<CoverageDto[]> {
  return apiRequest<CoverageDto[]>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/coverage`)
}

export async function fetchCaseResolution(caseId: string): Promise<ResolutionDto> {
  if (testDefault && LIVE_OFF()) return testDefault.resolution?.(caseId) ?? { state: 'none' }
  return apiRequest<ResolutionDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/resolution`)
}

/** A person's correction. This is allowed to replace what Desk holds; the close-time write is not. */
export async function pushCaseResolution(caseId: string, text: string, deskTicketId?: string | null): Promise<ResolutionDto> {
  if (testDefault?.pushResolution && LIVE_OFF()) return testDefault.pushResolution(caseId, text, deskTicketId)
  return apiRequest<ResolutionDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/resolution`, {
    method: 'POST',
    body: JSON.stringify({ text, desk_ticket_id: deskTicketId ?? null }),
  })
}

/** #443: raises the prepare_billing_line confirm card; nothing is billed until a person confirms it. */
export async function proposeBillingLine(caseId: string, line: BillingLineInDto): Promise<CardDto> {
  return apiRequest<CardDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/lines`, {
    method: 'POST',
    body: JSON.stringify(line),
  })
}

/** #832: stores the person's pricing rule only; it never mutates prepared lines. */
export async function decideQuoteBillingBasis(caseId: string, basis: 'fixed_quote' | 'time_and_materials'): Promise<void> {
  await apiRequest(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/quote-basis`, {
    method: 'POST',
    body: JSON.stringify({ basis }),
  })
}

/** #442: confirm (or add) the Books customer this case's customer bills under in one org. */
export async function confirmBooksLink(
  caseId: string,
  link: { books_org_id: BooksOrg; books_customer_id: string; books_name?: string | null },
): Promise<BooksLinkDto> {
  return apiRequest<BooksLinkDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/books-link`, {
    method: 'POST',
    body: JSON.stringify(link),
  })
}

export async function searchBooksCustomers(caseId: string, booksOrgId: BooksOrg, q: string): Promise<BooksCustomerCandidateDto[]> {
  const params = new URLSearchParams({ books_org_id: booksOrgId })
  if (q.trim()) params.set('q', q.trim())
  return apiRequest<BooksCustomerCandidateDto[]>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/books-customers?${params.toString()}`)
}

/** Active Books items for one org. A failed/incomplete lookup never becomes an empty list. */
export async function fetchBooksItems(booksOrgId: BooksOrg): Promise<BooksItemsDto> {
  const params = new URLSearchParams({ books_org_id: booksOrgId })
  return apiRequest<BooksItemsDto>(`/api/v1/billing/books/items?${params.toString()}`)
}

/** Freeze one case-level draft-invoice card. The server derives every payload byte from authoritative prepared lines. */
export async function proposeBooksDraft(caseId: string, notifyLea = true): Promise<CardDto> {
  const out = await apiRequest<{ card: CardDto }>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/books-draft`, {
    method: 'POST',
    body: JSON.stringify({ notify_lea: notifyLea }),
  })
  return out.card
}

/** A person's Retry after "Email Lea" failed on a created draft. Internal only; it never sends the invoice. */
export async function retryBooksDraftNotification(invoiceId: string): Promise<{ state: string; code?: string | null }> {
  return apiRequest<{ state: string; code?: string | null }>(`/api/v1/billing/books/invoices/${encodeURIComponent(invoiceId)}/notify`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

/** The labour source a SKU must carry (mirrors the API's guard: Labour-Sub-* is packet, other Labour-* is Desk). */
export function labourSourceFor(sku: string): 'desk_time' | 'packet' | 'none' {
  const s = sku.trim().toLowerCase()
  if (!s.startsWith('labour')) return 'none'
  return s.startsWith('labour-sub') ? 'packet' : 'desk_time'
}

export async function markNotBillable(
  caseId: string,
  reason: 'covered' | 'goodwill' | 'internal',
  note?: string,
): Promise<BillingLineDto[]> {
  return apiRequest<BillingLineDto[]>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/not-billable`, {
    method: 'POST',
    body: JSON.stringify({ reason, note: note || null }),
  })
}

/** Billing's question to the case owner. The note is required; `reasonCode` is the chip ("wrong_customer"). */
export async function markNeedsInfo(caseId: string, note: string, reasonCode?: string | null): Promise<void> {
  return apiRequest<void>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/needs-info`, {
    method: 'POST',
    body: JSON.stringify({ note, reason_code: reasonCode ?? null }),
  })
}

/** Billing's "Request fix": files a next move for the case owner. Writes nothing to Desk. */
export async function requestResolutionCorrection(caseId: string, text: string): Promise<void> {
  return apiRequest<void>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/resolution/correction-request`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
}

export interface LinkCandidateDto {
  books_invoice_id: string
  books_invoice_number: string
  reference_number: string
  status: 'draft' | 'sent' | 'paid' | 'void' | 'unknown'
  total?: string | number | null
  currency: string
  customer_matches: boolean
  already_linked: boolean
  books_invoice_url?: string | null
}

export interface LinkCandidatesDto {
  state: 'ok' | 'scope_missing' | 'unreachable' | 'incomplete' | 'books_org_unavailable'
  searched_references?: string[]
  candidates?: LinkCandidateDto[]
  scope?: string | null
  admin_integrations_href?: string | null
}

export async function fetchLinkCandidates(caseId: string, booksOrgId: string): Promise<LinkCandidatesDto> {
  const q = new URLSearchParams({ books_org_id: booksOrgId })
  return apiRequest<LinkCandidatesDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/books-invoice/candidates?${q.toString()}`)
}

export async function linkExistingInvoice(caseId: string, booksOrgId: string, booksInvoiceId: string): Promise<{ settled: boolean }> {
  return apiRequest<{ settled: boolean }>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/books-invoice/link`, {
    method: 'POST',
    body: JSON.stringify({ books_org_id: booksOrgId, books_invoice_id: booksInvoiceId }),
  })
}

export async function decideCoverage(
  caseId: string,
  domain: CoverageDomain,
  decision: CoverageDecision,
  basis: string,
): Promise<CoverageDto> {
  return apiRequest<CoverageDto>(`/api/v1/billing/cases/${encodeURIComponent(caseId)}/coverage`, {
    method: 'POST',
    body: JSON.stringify({ domain, decision, basis }),
  })
}

export async function searchBillingCustomers(q: string): Promise<SearchHitDto[]> {
  const hits = await apiRequest<SearchHitDto[]>(`/api/v1/search?q=${encodeURIComponent(q)}`)
  return hits.filter((hit) => hit.kind === 'customer')
}

export async function fetchPoExcessFlags(): Promise<StatusFlagDto[]> {
  if (testDefault && LIVE_OFF()) return testDefault.poExcessFlags?.() ?? []
  return apiRequest<StatusFlagDto[]>('/api/v1/flags?kind=over_nte&state=open')
}

export function exportCsvUrl(filters: BillingFilters): string {
  const params = new URLSearchParams({ view: filters.view === 'billed' ? 'billed' : 'active' })
  if (filters.periodFrom) params.set('period_from', filters.periodFrom)
  if (filters.periodTo) params.set('period_to', filters.periodTo)
  if (filters.booksOrgId) params.set('books_org_id', filters.booksOrgId)
  if (filters.customerId) params.set('customer_id', filters.customerId)
  if (filters.search) params.set('q', filters.search)
  if (filters.view !== 'billed') {
    if (filters.domain) params.set('domain', filters.domain)
    if (filters.hasContractorInvoice !== undefined) params.set('has_contractor_invoice', String(filters.hasContractorInvoice))
  }
  return `/api/v1/billing/export.csv?${params}`
}
